import { useRef } from 'react';
import {
  Check,
  ChevronDown,
  ExternalLink,
  FileText,
  Layers,
  Link2Off,
  MessageCircle,
  Package,
  Percent,
  Tags,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation, type TranslationKey } from '@dukanti/shared/i18n';
import { cn } from '@/lib/utils';
import { nextCardIndex, type DestinationCardId } from './destination-model';
import { DESTINATION_FOCUS_RING } from './focus-ring';

interface CardCopy {
  readonly label: TranslationKey;
  readonly description: TranslationKey;
  readonly icon: LucideIcon;
}

const CARD_COPY: Readonly<Record<DestinationCardId, CardCopy>> = {
  none: {
    label: 'admin.destination.none',
    description: 'admin.destination.noneDescription',
    icon: Link2Off,
  },
  sales: {
    label: 'admin.destination.sales',
    description: 'admin.destination.salesDescription',
    icon: Percent,
  },
  category: {
    label: 'admin.destination.category',
    description: 'admin.destination.categoryDescription',
    icon: Tags,
  },
  product: {
    label: 'admin.destination.product',
    description: 'admin.destination.productDescription',
    icon: Package,
  },
  external: {
    label: 'admin.destination.external',
    description: 'admin.destination.externalDescription',
    icon: ExternalLink,
  },
  'store-page': {
    label: 'admin.destination.storePage',
    description: 'admin.destination.storePageDescription',
    icon: FileText,
  },
  'custom-collection': {
    label: 'admin.destination.collection',
    description: 'admin.destination.collectionDescription',
    icon: Layers,
  },
  contact: {
    label: 'admin.destination.contact',
    description: 'admin.destination.contactDescription',
    icon: MessageCircle,
  },
};

export interface DestinationKindCardsProps {
  readonly primary: readonly DestinationCardId[];
  readonly more: readonly DestinationCardId[];
  readonly selected: DestinationCardId;
  /** "More destinations" is open (its cards join the group). */
  readonly moreOpen: boolean;
  readonly onMoreOpenChange: (open: boolean) => void;
  readonly onSelect: (card: DestinationCardId) => void;
  /** Accessible name of the group (the field's label). */
  readonly label: string;
  readonly describedBy?: string;
  readonly testIdPrefix: string;
}

/**
 * The destination cards (PD9, S03 adapted to dialog width): one column, a
 * single radio group with one tab stop. Arrow keys move **and** select
 * (the inline arrows mirror in RTL), Home/End go to the ends. The "More
 * destinations" cards join the group only while the disclosure is open; the
 * toggle sits after the group, so the group holds radios only.
 */
export function DestinationKindCards({
  primary,
  more,
  selected,
  moreOpen,
  onMoreOpenChange,
  onSelect,
  label,
  describedBy,
  testIdPrefix,
}: DestinationKindCardsProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const visible = moreOpen ? [...primary, ...more] : primary;
  const refs = useRef(new Map<DestinationCardId, HTMLButtonElement>());
  const selectedVisible = visible.includes(selected);
  const moreSelected = more.includes(selected);

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number): void => {
    const next = nextCardIndex(event.key, index, visible.length, dir);
    if (next === null) return;
    event.preventDefault();
    const card = visible[next];
    if (card === undefined) return;
    refs.current.get(card)?.focus();
    if (card !== selected) onSelect(card);
  };

  return (
    <div className="space-y-2">
      <div
        role="radiogroup"
        aria-label={label}
        aria-describedby={describedBy}
        className="grid gap-2"
        data-testid={`${testIdPrefix}-cards`}
      >
        {visible.map((card, index) => {
          const copy = CARD_COPY[card];
          const Icon = copy.icon;
          const checked = card === selected;
          // One tab stop: the checked card, or the first when the checked one is hidden.
          const tabbable = checked || (!selectedVisible && index === 0);
          return (
            <button
              key={card}
              ref={(node) => {
                if (node) refs.current.set(card, node);
                else refs.current.delete(card);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={tabbable ? 0 : -1}
              onClick={() => {
                if (!checked) onSelect(card);
              }}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'flex min-h-11 w-full items-start gap-3 rounded-lg border p-3 text-start outline-none transition-colors',
                DESTINATION_FOCUS_RING,
                checked
                  ? 'border-admin-lime bg-admin-lime-light/30 ring-admin-lime/30 ring-2'
                  : 'border-border bg-card hover:bg-admin-surface-muted',
              )}
              data-testid={`${testIdPrefix}-card-${card}`}
            >
              <Icon className="text-zaatar mt-0.5 size-4 shrink-0" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="text-foreground block text-sm font-medium">{t(copy.label)}</span>
                <span className="text-muted-foreground mt-0.5 block text-xs leading-5">
                  {t(copy.description)}
                </span>
              </span>
              {/* A radio mark: checked is not told by the lime border alone. */}
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
                  checked
                    ? 'border-zaatar-deep bg-zaatar-deep dark:border-zaatar-soft dark:bg-zaatar-soft text-white dark:text-black'
                    : 'border-input bg-card',
                )}
                data-testid={`${testIdPrefix}-card-${card}-mark`}
              >
                {checked ? <Check className="size-3" strokeWidth={3} /> : null}
              </span>
            </button>
          );
        })}
      </div>
      {/* A checked card behind the disclosure keeps it open (PD9): no toggle then. */}
      {more.length > 0 && !(moreOpen && moreSelected) ? (
        <button
          type="button"
          aria-expanded={moreOpen}
          onClick={() => onMoreOpenChange(!moreOpen)}
          className={cn(
            'text-foreground hover:bg-admin-surface-muted flex min-h-11 w-full items-center justify-between gap-2 rounded-lg px-3 text-start text-sm font-medium outline-none',
            DESTINATION_FOCUS_RING,
          )}
          data-testid={`${testIdPrefix}-more`}
        >
          <span>{t(moreOpen ? 'admin.destination.fewer' : 'admin.destination.more')}</span>
          <ChevronDown
            aria-hidden
            className={cn('size-4 shrink-0 transition-transform', moreOpen && 'rotate-180')}
          />
        </button>
      ) : null}
    </div>
  );
}
